import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { DataTable } from "@/components/ui/data-table";
import { API_ENDPOINTS } from "../../api/header";
import apiClient from "../../api/apiClient";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ReactSelect } from "@/components/ui/react-select";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ProductCategoryModal from "./components/ProductCategoryModal";
import {
  PlusCircle,
  Edit,
  Trash2,
  List,
  MoreVertical,
  Search,
  ChevronRight,
  ChevronDown,
  Image as ImageIcon,
  GripVertical,
  ArrowUpDown,
  ArrowDownAZ,
  ArrowUpZA,
  Save,
  X,
  Tag,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { PageContainer } from "@/components/layout/PageContainer";
import RecordNotFound from "@/core/components/RecordNotFound";

// Draggable row component for reorder mode
const DraggableRow = ({ category, allItems, onParentChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const indent = category._depth ?? 0;

  // Find all descendant IDs of this category to prevent circular parenting
  const descendantIds = useMemo(() => {
    const ids = new Set();
    const findDescendants = (pid) => {
      allItems.forEach((item) => {
        if (item.parent_id === pid && !ids.has(item.id)) {
          ids.add(item.id);
          findDescendants(item.id);
        }
      });
    };
    findDescendants(category.id);
    return ids;
  }, [allItems, category.id]);

  const parentOptions = useMemo(
    () => [
      { value: null, label: "- None (Root level)" },
      ...allItems
        .filter((i) => i.id !== category.id && !descendantIds.has(i.id))
        .map((i) => ({
          value: i.id,
          label: `${(i._depth ?? 0) > 0 ? "\u00A0\u00A0".repeat(i._depth) + "└─ " : ""}${i.name}`,
        })),
    ],
    [allItems, category.id, descendantIds],
  );

  const currentParentOption =
    parentOptions.find((o) => o.value === (category.parent_id ?? null)) ||
    parentOptions[0];

  const isFeatured = Boolean(
    category.is_featured === true ||
    category.is_featured === 1 ||
    category.is_featured === "1" ||
    category.is_featured === "true",
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 bg-card border rounded-md mb-2 shadow-sm ${isDragging ? "ring-2 ring-primary" : ""
        }`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none flex-shrink-0"
        type="button"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div
        className="flex items-center gap-2 min-w-0"
        style={{ paddingLeft: `${indent * 20}px`, flex: "1 1 0" }}
      >
        <img
          src={
            category.media?.[0]?.url ||
            category.image ||
            "assets/img/product/noimage.png"
          }
          alt=""
          className="h-7 w-7 rounded object-cover border flex-shrink-0"
        />
        <span
          className={`truncate text-sm ${indent === 0 ? "font-semibold text-foreground" : "text-muted-foreground"
            }`}
        >
          {indent > 0 && <span className="mr-1 opacity-50">└─</span>}
          {category.name}
        </span>
        {isFeatured && (
          <Badge variant="warning" className="text-[10px] py-0 px-1.5 ml-1">
            Featured
          </Badge>
        )}
      </div>
      <div
        className="flex-shrink-0 w-56"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <ReactSelect
          value={currentParentOption}
          onChange={(opt) => onParentChange(category.id, opt?.value ?? null)}
          options={parentOptions}
          isClearable={false}
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : null
          }
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          placeholder="Root category"
        />
      </div>
    </div>
  );
};

const CategoryList = () => {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [expandedIds, setExpandedIds] = useState(new Set());

  const [showModal, setShowModal] = useState(false);
  const [initialCategoryName, setInitialCategoryName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Reorder state
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderItems, setReorderItems] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (perm) => permissions && permissions.includes(perm);

  // Queries
  const { data: categoryData, isLoading: isCategoryLoading } = useQuery({
    queryKey: ["categories", searchText],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.CATEGORIES, {
        params: {
          perPage: 9999,
          search: searchText || undefined,
        },
      });
      return res.data;
    },
  });

  const categorylist = useMemo(() => {
    if (Array.isArray(categoryData)) return categoryData;
    if (Array.isArray(categoryData?.data)) return categoryData.data;
    return [];
  }, [categoryData]);

  const deleteCategoryMutation = useMutation({
    mutationFn: async (id) =>
      await apiClient.delete(API_ENDPOINTS.INVENTORY.CATEGORY_DETAILS(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["categories-all"] });
      queryClient.invalidateQueries({ queryKey: ["pos-categories-all"] });
      queryClient.invalidateQueries({ queryKey: ["pos-subcategories"] });
      toast.success("Category deleted successfully");
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || "Failed to delete category"),
  });

  // Fetch all categories for reorder (no pagination)
  const { data: allCategoryData, isLoading: isLoadingAll } = useQuery({
    queryKey: ["categories-all"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.CATEGORIES, {
        params: { perPage: 9999 },
      });
      return res.data?.data || res.data || [];
    },
    enabled: reorderMode,
  });

  const reorderMutation = useMutation({
    mutationFn: async (categories) =>
      await apiClient.post(API_ENDPOINTS.INVENTORY.CATEGORIES_REORDER, {
        categories,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["categories-all"] });
      queryClient.invalidateQueries({ queryKey: ["pos-categories-all"] });
      queryClient.invalidateQueries({ queryKey: ["pos-subcategories"] });
      toast.success("Category order saved successfully");
      setReorderMode(false);
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || "Failed to save order"),
  });

  // Build flat ordered list for reorder mode (hierarchy-aware, preserving API response order)
  const buildFlatReorderList = useCallback((cats) => {
    const map = new Map();
    cats.forEach((c) => map.set(String(c.id), { ...c, children: [] }));
    const roots = [];
    cats.forEach((c) => {
      const parentId = c.parent_id != null && c.parent_id !== "" ? String(c.parent_id) : null;
      if (parentId && map.has(parentId)) {
        map.get(parentId).children.push(map.get(String(c.id)));
      } else {
        roots.push(map.get(String(c.id)));
      }
    });
    const flatten = (nodes, depth = 0) => {
      const result = [];
      nodes.forEach((n) => {
        result.push({ ...n, _depth: depth, level: depth });
        if (n.children?.length) result.push(...flatten(n.children, depth + 1));
      });
      return result;
    };
    return flatten(roots);
  }, []);

  // Populate reorder items when entering reorder mode
  useEffect(() => {
    if (reorderMode && allCategoryData) {
      const allCats = Array.isArray(allCategoryData)
        ? allCategoryData
        : Array.isArray(allCategoryData?.data)
          ? allCategoryData.data
          : [];
      setReorderItems(buildFlatReorderList(allCats));
    }
  }, [reorderMode, allCategoryData, buildFlatReorderList]);

  const handleDragStart = ({ active }) => setActiveId(active.id);

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;

    setReorderItems((prev) => {
      const activeIndex = prev.findIndex((i) => i.id === active.id);
      const overIndex = prev.findIndex((i) => i.id === over.id);
      if (activeIndex === -1 || overIndex === -1) return prev;

      const activeItem = prev[activeIndex];
      const overItem = prev[overIndex];

      // Find all descendants of activeItem
      const descendantIds = new Set();
      const findDescendants = (pid) => {
        prev.forEach((it) => {
          if (it.parent_id === pid && !descendantIds.has(it.id)) {
            descendantIds.add(it.id);
            findDescendants(it.id);
          }
        });
      };
      findDescendants(active.id);

      // Cannot drop inside own subtree
      if (descendantIds.has(over.id)) return prev;

      // Extract subtree block (activeItem + all its descendants)
      const subtree = prev.filter(
        (i) => i.id === active.id || descendantIds.has(i.id),
      );
      const withoutSubtree = prev.filter(
        (i) => i.id !== active.id && !descendantIds.has(i.id),
      );

      // Target index in withoutSubtree
      const targetIndex = withoutSubtree.findIndex((i) => i.id === over.id);
      if (targetIndex === -1) return prev;

      // Determine insert position
      let insertIndex = targetIndex;
      if (activeIndex < overIndex) {
        // Dragging downwards: insert AFTER overItem's whole subtree
        insertIndex = targetIndex + 1;
        while (
          insertIndex < withoutSubtree.length &&
          (withoutSubtree[insertIndex]._depth ?? 0) > (overItem._depth ?? 0)
        ) {
          insertIndex++;
        }
      }

      // Check parent adjustment:
      // If activeItem is a root category (_depth === 0), it stays a root category (parent_id: null)
      // If activeItem is a subcategory and moved into a new parent context:
      let updatedSubtree = subtree;
      if ((activeItem._depth ?? 0) > 0 && activeItem.parent_id !== overItem.parent_id) {
        const targetParentId = overItem.parent_id ?? null;
        const targetParent = targetParentId
          ? withoutSubtree.find((i) => i.id === targetParentId)
          : null;
        const newDepth = targetParent ? (targetParent._depth ?? 0) + 1 : 0;
        const newLevel = targetParent ? (targetParent.level ?? 0) + 1 : 0;
        const depthDelta = newDepth - (activeItem._depth ?? 0);

        updatedSubtree = subtree.map((i) => {
          if (i.id === active.id) {
            return {
              ...i,
              parent_id: targetParentId,
              _depth: newDepth,
              level: newLevel,
            };
          }
          return {
            ...i,
            _depth: Math.max(0, (i._depth ?? 0) + depthDelta),
            level: Math.max(0, (i.level ?? 0) + depthDelta),
          };
        });
      }

      const result = [...withoutSubtree];
      result.splice(insertIndex, 0, ...updatedSubtree);
      return result;
    });
  };

  const handleSaveReorder = () => {
    const payload = reorderItems.map((item, index) => ({
      id: Number(item.id),
      parent_id:
        item.parent_id !== null &&
          item.parent_id !== undefined &&
          item.parent_id !== ""
          ? Number(item.parent_id)
          : null,
      level: Math.max(0, Number(item.level ?? item._depth ?? 0)),
      sort_order: index,
    }));
    reorderMutation.mutate(payload);
  };

  const handleCancelReorder = () => {
    setReorderMode(false);
    setReorderItems([]);
  };

  const handleSortAlphabetical = useCallback((direction = "asc") => {
    setReorderItems((prev) => {
      if (!prev || prev.length === 0) return prev;

      // Build map by string ID to preserve types
      const itemMap = new Map();
      prev.forEach((item) => {
        itemMap.set(String(item.id), { ...item, children: [] });
      });

      // Group nodes into parent-child hierarchy
      const roots = [];
      prev.forEach((item) => {
        const node = itemMap.get(String(item.id));
        const pId =
          item.parent_id !== null &&
            item.parent_id !== undefined &&
            item.parent_id !== ""
            ? String(item.parent_id)
            : null;
        if (pId && itemMap.has(pId) && pId !== String(item.id)) {
          itemMap.get(pId).children.push(node);
        } else {
          roots.push(node);
        }
      });

      // Natural alphabetical comparator
      const compareNodes = (a, b) => {
        const nameA = (a.name || "").trim();
        const nameB = (b.name || "").trim();
        const result =
          nameA.localeCompare(nameB, undefined, {
            numeric: true,
            sensitivity: "base",
          }) || nameA.localeCompare(nameB);
        return direction === "desc" ? -result : result;
      };

      // Recursively sort siblings at each level (parents first, then direct children, etc.)
      const sortSubtree = (nodes) => {
        nodes.sort(compareNodes);
        nodes.forEach((node) => {
          if (node.children && node.children.length > 0) {
            sortSubtree(node.children);
          }
        });
      };

      sortSubtree(roots);

      // Flatten tree in pre-order depth-first traversal
      const flatten = (nodes, depth = 0, visited = new Set()) => {
        const result = [];
        nodes.forEach((node) => {
          const key = String(node.id);
          if (visited.has(key)) return;
          visited.add(key);

          const { children, ...rest } = node;
          result.push({
            ...rest,
            _depth: depth,
            level: depth,
          });

          if (children && children.length > 0) {
            result.push(...flatten(children, depth + 1, visited));
          }
        });
        return result;
      };

      const sortedList = flatten(roots);

      // Safeguard: append any orphaned items if there was an unusual loop
      if (sortedList.length < prev.length) {
        const visitedKeys = new Set(sortedList.map((i) => String(i.id)));
        const missing = prev.filter((i) => !visitedKeys.has(String(i.id)));
        missing.sort(compareNodes);
        sortedList.push(...missing);
      }

      toast.success(
        direction === "desc"
          ? "Categories sorted alphabetically (Z to A)"
          : "Categories sorted alphabetically (A to Z)",
      );

      return sortedList;
    });
  }, []);

  const handleParentChange = useCallback((itemId, newParentId) => {
    setReorderItems((prev) => {
      const itemIndex = prev.findIndex((i) => i.id === itemId);
      if (itemIndex === -1) return prev;
      const item = prev[itemIndex];

      // Find all descendants of itemId
      const descendantIds = new Set();
      const findDescendants = (pid) => {
        prev.forEach((it) => {
          if (it.parent_id === pid && !descendantIds.has(it.id)) {
            descendantIds.add(it.id);
            findDescendants(it.id);
          }
        });
      };
      findDescendants(itemId);

      // Prevent circular parenting
      if (newParentId === itemId || descendantIds.has(newParentId)) {
        toast.error("Cannot set a child category as parent");
        return prev;
      }

      const parentItem = newParentId
        ? prev.find((i) => i.id === newParentId)
        : null;
      const newDepth = parentItem ? (parentItem._depth ?? 0) + 1 : 0;
      const newLevel = parentItem ? (parentItem.level ?? 0) + 1 : 0;
      const depthDelta = newDepth - (item._depth ?? 0);

      // Extract subtree
      const subtree = prev
        .filter((i) => i.id === itemId || descendantIds.has(i.id))
        .map((i) => {
          if (i.id === itemId) {
            return {
              ...i,
              parent_id: newParentId ?? null,
              level: newLevel,
              _depth: newDepth,
            };
          }
          return {
            ...i,
            level: Math.max(0, (i.level ?? 0) + depthDelta),
            _depth: Math.max(0, (i._depth ?? 0) + depthDelta),
          };
        });

      const withoutSubtree = prev.filter(
        (i) => i.id !== itemId && !descendantIds.has(i.id),
      );

      if (!newParentId) {
        return [...withoutSubtree, ...subtree];
      }

      const parentIdx = withoutSubtree.findIndex((i) => i.id === newParentId);
      if (parentIdx === -1) {
        return [...withoutSubtree, ...subtree];
      }

      let insertIdx = parentIdx + 1;
      while (
        insertIdx < withoutSubtree.length &&
        (withoutSubtree[insertIdx]._depth ?? 0) > (withoutSubtree[parentIdx]._depth ?? 0)
      ) {
        insertIdx++;
      }

      const updated = [...withoutSubtree];
      updated.splice(insertIdx, 0, ...subtree);
      return updated;
    });
  }, []);

  const buildHierarchicalData = React.useCallback(
    (categories) => {
      const categoryMap = new Map();
      categories.forEach((cat) =>
        categoryMap.set(String(cat.id), { ...cat, children: [] }),
      );
      const rootCategories = [];
      categories.forEach((cat) => {
        const parentId =
          cat.parent_id !== null &&
            cat.parent_id !== undefined &&
            cat.parent_id !== ""
            ? String(cat.parent_id)
            : null;
        if (parentId && categoryMap.has(parentId)) {
          categoryMap.get(parentId).children.push(categoryMap.get(String(cat.id)));
        } else {
          rootCategories.push(categoryMap.get(String(cat.id)));
        }
      });
      const flattenHierarchy = (categories, depth = 0) => {
        const result = [];
        categories.forEach((cat) => {
          const hasChildren = cat.children && cat.children.length > 0;
          const isExpanded = searchText ? true : expandedIds.has(cat.id);
          result.push({ ...cat, depth, hasChildren, isExpanded });
          if (hasChildren && isExpanded)
            result.push(...flattenHierarchy(cat.children, depth + 1));
        });
        return result;
      };
      return flattenHierarchy(rootCategories);
    },
    [expandedIds, searchText],
  );

  const filteredData = useMemo(() => {
    return buildHierarchicalData(categorylist);
  }, [categorylist, buildHierarchicalData]);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleAdd = (prefilledName = "") => {
    setIsEditing(false);
    setCurrentCategory(null);
    setInitialCategoryName(
      typeof prefilledName === "string" ? prefilledName : searchText.trim(),
    );
    setShowModal(true);
  };

  const handleEdit = (id) => {
    const category = categorylist.find((cat) => cat.id === id);
    if (category) {
      setCurrentCategory(category);
      setIsEditing(true);
      setShowModal(true);
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (deleteId) {
      deleteCategoryMutation.mutate(deleteId);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  // columns definition removed

  const activeItem = activeId
    ? reorderItems.find((i) => i.id === activeId)
    : null;

  if (!hasPermission("category product read")) {
    return <RecordNotFound />;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Categories"
          subtitle="Manage your product categories"
        />
        <PageHeaderActions>
          {reorderMode ? (
            <>
              <div className="inline-flex rounded-md shadow-sm">
                <Button
                  variant="outline"
                  className="rounded-r-none border-r-0"
                  onClick={() => handleSortAlphabetical("asc")}
                  disabled={
                    reorderMutation.isPending ||
                    isLoadingAll ||
                    reorderItems.length === 0
                  }
                  title="Sort parent categories and subcategories alphabetically (A to Z)"
                >
                  <ArrowDownAZ className="mr-2 h-4 w-4" /> Sort Alphabetically
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-l-none px-2 w-8"
                      disabled={
                        reorderMutation.isPending ||
                        isLoadingAll ||
                        reorderItems.length === 0
                      }
                      title="More sorting options"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => handleSortAlphabetical("asc")}
                    >
                      <ArrowDownAZ className="mr-2 h-4 w-4" /> Sort A to Z (Ascending)
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleSortAlphabetical("desc")}
                    >
                      <ArrowUpZA className="mr-2 h-4 w-4" /> Sort Z to A (Descending)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <Button
                variant="outline"
                onClick={handleCancelReorder}
                disabled={reorderMutation.isPending}
              >
                <X className="mr-2 h-4 w-4" /> Cancel
              </Button>
              <Button
                onClick={handleSaveReorder}
                disabled={reorderMutation.isPending}
              >
                <Save className="mr-2 h-4 w-4" />
                {reorderMutation.isPending ? "Saving..." : "Save Order"}
              </Button>
            </>
          ) : (
            <>
              {hasPermission("category product update") && (
                <Button variant="outline" onClick={() => setReorderMode(true)}>
                  <ArrowUpDown className="mr-2 h-4 w-4" /> Reorder
                </Button>
              )}
              {hasPermission("category product create") && (
                <Button onClick={handleAdd}>
                  <PlusCircle className="mr-2 h-4 w-4" /> Add Category
                </Button>
              )}
            </>
          )}
        </PageHeaderActions>
      </PageHeader>

      {reorderMode ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-muted/40 rounded-md border border-dashed">
            <GripVertical className="h-4 w-4 flex-shrink-0" />
            <span>
              Drag the <GripVertical className="inline h-3 w-3" /> handle to
              reorder, or click <strong>Sort Alphabetically</strong> to auto-sort
              parent categories and subcategories. Use the{" "}
              <strong>Parent</strong> dropdown on each row to change nesting.
              Click <strong>Save Order</strong> when done.
            </span>
          </div>
          {isLoadingAll ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : (
            <DndContext
              sensors={dndSensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={reorderItems.map((i) => i.id)}
                strategy={verticalListSortingStrategy}
              >
                <div>
                  {reorderItems.map((cat) => (
                    <DraggableRow
                      key={cat.id}
                      category={cat}
                      allItems={reorderItems}
                      onParentChange={handleParentChange}
                    />
                  ))}
                </div>
              </SortableContext>
              <DragOverlay>
                {activeItem ? (
                  <div className="flex items-center gap-3 p-3 bg-card border-2 border-primary rounded-md shadow-lg opacity-90">
                    <GripVertical className="h-4 w-4 text-primary flex-shrink-0" />
                    <img
                      src={
                        activeItem.media?.[0]?.url ||
                        activeItem.image ||
                        "assets/img/product/noimage.png"
                      }
                      alt=""
                      className="h-7 w-7 rounded object-cover border flex-shrink-0"
                    />
                    <span className="text-sm font-medium">
                      {activeItem.name}
                    </span>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </div>
      ) : (
        <>
          <FilterCard>
            <FilterSearch
              placeholder="Search categories..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </FilterCard>

          {isCategoryLoading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground bg-card border rounded-md shadow-sm gap-3">
              <Tag className="h-8 w-8 text-muted-foreground/50" />
              <p className="text-sm">
                {searchText.trim() ? (
                  <>
                    No categories found matching &quot;
                    <span className="font-semibold text-foreground">
                      {searchText.trim()}
                    </span>
                    &quot;.
                  </>
                ) : (
                  "No categories found."
                )}
              </p>
              {hasPermission("category product create") && searchText.trim() && (
                <Button
                  size="sm"
                  className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm"
                  onClick={() => handleAdd(searchText.trim())}
                >
                  <PlusCircle className="h-4 w-4 mr-1.5" />
                  Add &quot;{searchText.trim()}&quot;
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-0.5">
              {filteredData.map((record) => {
                const indent = record.depth || 0;
                return (
                  <div
                    key={record.id}
                    className="flex items-center gap-3 p-3 bg-card border rounded-md shadow-sm transition-all hover:border-primary/50"
                  >
                    <div
                      className="flex items-center gap-2 min-w-0 flex-1"
                      style={{ paddingLeft: `${indent * 24}px` }}
                    >
                      {record.hasChildren ? (
                        <button
                          onClick={() => toggleExpand(record.id)}
                          className="mr-1 p-1 hover:bg-muted rounded text-muted-foreground transition-colors flex-shrink-0"
                          title={record.isExpanded ? "Collapse" : "Expand"}
                        >
                          {record.isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      ) : indent > 0 ? (
                        <span className="text-muted-foreground mr-2 ml-6 flex-shrink-0">
                          {"└─ "}
                        </span>
                      ) : (
                        <div className="w-6 mr-1" />
                      )}
                      <img
                        src={
                          record.media?.[0]?.url ||
                          record.image ||
                          "assets/img/product/noimage.png"
                        }
                        alt="Category"
                        className="h-10 w-10 rounded object-cover border flex-shrink-0"
                      />
                      <div className="flex flex-col overflow-hidden">
                        <span
                          className={`truncate text-base ${indent === 0 ? "font-semibold" : ""
                            }`}
                        >
                          {record.name}
                        </span>
                        {record.parent && (
                          <span className="text-xs text-muted-foreground truncate">
                            Parent: {record.parent.name}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4 flex-shrink-0 mr-4">
                      {record.is_featured && (
                        <Badge variant="warning">Featured</Badge>
                      )}
                      <Badge
                        variant={record.is_active ? "success" : "destructive"}
                      >
                        {record.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>

                    <div className="flex-shrink-0">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {hasPermission("category product update") && (
                            <DropdownMenuItem
                              onClick={() => handleEdit(record.id)}
                            >
                              <Edit className="mr-2 h-4 w-4" /> Edit
                            </DropdownMenuItem>
                          )}
                          {hasPermission("category product delete") && (
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleDelete(record.id)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <ProductCategoryModal
        open={showModal}
        onOpenChange={(open) => {
          setShowModal(open);
          if (!open) {
            setIsEditing(false);
            setCurrentCategory(null);
            setInitialCategoryName("");
          }
        }}
        categoryToEdit={isEditing ? currentCategory : null}
        initialCategoryName={initialCategoryName}
        categories={categorylist}
        isLoadingCategories={isCategoryLoading}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["categories"] });
          queryClient.invalidateQueries({ queryKey: ["categories-all"] });
          queryClient.invalidateQueries({ queryKey: ["pos-categories-all"] });
          queryClient.invalidateQueries({ queryKey: ["pos-subcategories"] });
          setSearchText("");
          setInitialCategoryName("");
        }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              category.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};
export default CategoryList;
